import { useState } from "react";
import { theme } from "../theme.js";
import FilterDropdown from "./FilterDropdown.jsx";

export default function FilterPanel({
  sportsList = [],
  selectedCategories = [],
  onCategoriesChange,
  selectedDistances = [],
  onDistancesChange,
  minDistance = "",
  maxDistance = "",
  onDistanceMinMaxChange,
  dateRangeStart = null,
  dateRangeEnd = null,
  onDateRangeChange,
}) {
  const [openDropdown, setOpenDropdown] = useState(null);

  return (
    <div style={styles.filterPanelWrapper}>
      {/* Top Section - Centered */}
      <div style={styles.filterTopSection}>
        <div style={styles.filterContainer}>
          <FilterDropdown
            options={sportsList}
            selectedFilters={selectedCategories}
            onFiltersChange={onCategoriesChange}
            label="Type de course"
            onOpen={() => {
              setOpenDropdown("categories");
            }}
            shouldClose={openDropdown !== "categories" && openDropdown !== null}
          />

          <FilterDropdown
            selectedFilters={selectedDistances}
            onFiltersChange={onDistancesChange}
            label="Distance du parcours"
            filterType="distance"
            minValue={minDistance}
            maxValue={maxDistance}
            onIntervalChange={(min, max) => {
              if (onDistanceMinMaxChange) {
                onDistanceMinMaxChange(min, max);
              }
            }}
            onOpen={() => {
              setOpenDropdown("distances");
              // Don't setOpenDropdown(null) here, keep it so other dropdowns close
            }}
            shouldClose={openDropdown !== "distances" && openDropdown !== null}
            align="center"
          />

          <FilterDropdown
            label="Date"
            filterType="date"
            minValue={dateRangeStart}
            maxValue={dateRangeEnd}
            onIntervalChange={onDateRangeChange}
            onOpen={() => {
              setOpenDropdown("date");
            }}
            shouldClose={openDropdown !== "date" && openDropdown !== null}
            align="right"
          />
        </div>
      </div>

      {/* Bottom Section - Left Aligned */}
      <div style={styles.filterBottomSection}>
        <div style={styles.activeFiltersContainer}>
          {selectedCategories.length > 0 && (
            <div style={styles.filterTag}>
              <span style={{ textTransform: "capitalize" }}>
                {selectedCategories.map(id => {
                  // Find the sport name by ID from sportsList
                  const sport = sportsList.find(s => s.id === id);
                  return sport ? sport.name : id;
                }).join(", ")}
              </span>
              <button
                onClick={() => onCategoriesChange([])}
                style={styles.filterTagButton}
                title="Remove filter"
              >
                ✕
              </button>
            </div>
          )}

          {(minDistance || maxDistance) && (
            <div style={styles.filterTag}>
              <span>
                {minDistance && maxDistance
                  ? `Entre ${minDistance} km et ${maxDistance} km`
                  : minDistance
                  ? `Minimum ${minDistance} km`
                  : `Maximum ${maxDistance} km`}
              </span>
              <button
                onClick={() => {
                  if (onDistanceMinMaxChange) {
                    onDistanceMinMaxChange("", "");
                  }
                }}
                style={styles.filterTagButton}
                title="Remove filter"
              >
                ✕
              </button>
            </div>
          )}

          {selectedDistances.length > 0 && (
            <div style={styles.filterTag}>
              <span>
                {selectedDistances.map(f => f).join(", ")}
              </span>
              <button
                onClick={() => onDistancesChange([])}
                style={styles.filterTagButton}
                title="Remove filter"
              >
                ✕
              </button>
            </div>
          )}

          {dateRangeStart && dateRangeEnd && (
            <div style={styles.filterTag}>
              <span>
                {`${new Date(dateRangeStart).toLocaleDateString()} - ${new Date(dateRangeEnd).toLocaleDateString()}`}
              </span>
              <button
                onClick={() => onDateRangeChange(null, null)}
                style={styles.filterTagButton}
                title="Remove filter"
              >
                ✕
              </button>
            </div>
          )}
        </div>

        {(selectedCategories.length > 0 || selectedDistances.length > 0 || minDistance || maxDistance || dateRangeStart || dateRangeEnd) && (
          <button
            onClick={() => {
              onCategoriesChange([]);
              onDistancesChange([]);
              onDistanceMinMaxChange("", "");
              onDateRangeChange(null, null);
            }}
            style={styles.clearAllButton}
          >
            Clear All
          </button>
        )}
      </div>
    </div>
  );
}

const styles = {
  filterPanelWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.lg,
    width: "100%",
  },
  filterTopSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
  },
  filterContainer: {
    display: "flex",
    gap: theme.spacing.md,
    justifyContent: "center",
    flexWrap: "wrap",
  },
  filterButton: {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    color: theme.colors.white,
    border: `2px solid rgba(255, 255, 255, 0.3)`,
    borderRadius: theme.radius.full,
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.medium,
    cursor: "pointer",
    transition: `all ${theme.transitions.base}`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
  },
  filterButtonActive: {
    backgroundColor: theme.colors.light,
    color: theme.colors.dark,
    borderColor: theme.colors.light,
    marginBottom: theme.spacing.sm,
  },
  filterBottomSection: {
    display: "flex",
    flexDirection: "column",
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
    alignItems: "flex-start",
    width: "100%",
    gap: theme.spacing.sm,
  },
  activeFiltersContainer: {
    display: "flex",
    gap: theme.spacing.md,
    flexWrap: "wrap",
    justifyContent: "flex-start",
    minHeight: "40px",
  },
  clearAllButton: {
    backgroundColor: "transparent",
    color: theme.colors.white,
    border: "none",
    borderBottom: `1px solid ${theme.colors.white}`,
    padding: `${theme.spacing.xs} 0`,
    fontSize: theme.fonts.size.sm,
    cursor: "pointer",
    transition: `all ${theme.transitions.base}`,
    marginLeft: theme.spacing.md,
    whiteSpace: "nowrap",
  },
  filterTag: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    color: theme.colors.white,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: theme.radius.full,
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.medium,
    border: `1px solid rgba(255, 255, 255, 0.3)`,
  },
  filterTagButton: {
    backgroundColor: "transparent",
    color: theme.colors.white,
    border: "none",
    cursor: "pointer",
    fontSize: theme.fonts.size.sm,
    padding: 0,
    marginLeft: theme.spacing.xs,
    transition: `all ${theme.transitions.base}`,
    opacity: 0.8,
  },
};
